import {
    Icon,
    IconButton,
    Tooltip,
} from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { StandardTable } from '../../_components/table/standard.table';
import { PaperTableHeader } from '../../_components/table/header.table';
import { TABLE_FILTER } from '../../_const/tableFilter.const';
import { handleErrorResponse } from '../../_helpers/handleResponse.helper';
import { SearchTableHeader } from '../../_components/table/search.table';
import { clientService } from '../../_services/client.service';
import { accountService } from '../../_services/accounts.service';
import { ClientManageDrawer } from './client-manageDrawer';
import { getDateString } from '../../_helpers/dateTime.helper';
import { DataTypeEnum } from '../../_common/enum/dataType.enum';
import { toastr } from '../../_components/toastr/toastr';


export function ClientIndex() {
    const [data, setData] = useState<any>({ items: [], count: 10 });
    const [filter, setFilter] = useState({ ...TABLE_FILTER });
    const [isLoading, setIsLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(() => accountService.isAdmin);
    const navigate = useNavigate();

    useEffect(() => {
        const sub = accountService.currentUser.subscribe(() => setIsAdmin(accountService.isAdmin));
        return () => sub.unsubscribe();
    }, []);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        return await clientService.getAll(filter)
            .then(
                json => {
                    setData(json);
                }, handleErrorResponse)
            .finally(() => {
                setIsLoading(false);
            });
    }, [filter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDeactivate = useCallback((id: number, displayName: string) => {
        if (!window.confirm(`Deactivate client "${displayName}"? They will be removed from the default list (soft delete).`)) {
            return;
        }
        return clientService.disable(id)
            .then(
                () => {
                    toastr.success('Client deactivated');
                    return fetchData();
                },
                handleErrorResponse
            );
    }, [fetchData]);

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                id: 'actions',
                header: 'Actions',
                size: isAdmin ? 120 : 80,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <>
                        <Tooltip title="View">
                            <IconButton
                                color="primary"
                                onClick={() => { navigate("Details/" + row.original.id) }}>
                                <Icon>visibility</Icon>
                            </IconButton>
                        </Tooltip>
                        {isAdmin && row.original.isActive && (
                            <Tooltip title="Deactivate client">
                                <IconButton
                                    color="error"
                                    onClick={() => handleDeactivate(
                                        row.original.id,
                                        String(row.original.name ?? row.original.id)
                                    )}>
                                    <Icon>block</Icon>
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                ),
            },
            {
                accessorKey: 'id',
                header: 'ID',
            },
            {
                accessorKey: 'centreName',
                header: 'Centre',
            },
            {
                accessorKey: 'name',
                header: 'Name',
            },
            {
                accessorKey: 'nickname',
                header: 'Nickname',
            },
            {
                accessorKey: 'phone',
                header: 'Phone',
            },
            {
                accessorKey: 'dob',
                header: 'DOB',
                Cell: ({ row }) => (
                    <>{!!row.original.dob ? getDateString(row.original.dob) : ""}</>
                ),
            },
            {
                accessorKey: 'mobilePhone',
                header: 'Mobile Phone',
            },
            {
                accessorKey: 'isActive',
                header: 'Status',
                Cell: ({ row }) => (
                    <> {row.original.isActive ? "Active" : "Inactive"} </>
                )
            },
        ],
        [isAdmin, navigate, handleDeactivate]
    );

    return (
        <>
            <PaperTableHeader title="Client" buttons={[
                { icon: "add", label: "Create", onClick: () => { navigate("Create") } }
            ]} />

            <SearchTableHeader inputs={[
                { label: "Centre", name: "centreId", type: "autocomplete", dataType: DataTypeEnum.CentreByUser, onChange: setFilter, value: filter.centreId, width: 250 },
                { label: "Name", name: "name", onChange: setFilter, value: filter.name },
            ]} />

            <StandardTable
                columns={columns}
                data={data}
                filter={filter}
                setFilter={setFilter}
                loading={isLoading}
            />

            <Routes>
               <Route path="Create" element={<ClientManageDrawer onSave={() => { navigate(""); fetchData(); }} onClose={() => { navigate("") }} />} />

            </Routes>

        </>
    );
}
